import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type ShippingRateDetails = OutputRouter['shippingRates']['getById']['shippingRate']
export type ShippingRatesListOutput = OutputRouter['shippingRates']['list']
export type ShippingRateItem = ShippingRatesListOutput['items'][number]
export type CreateShippingRateOutput = OutputRouter['shippingRates']['create']
export type UpdateShippingRateOutput = OutputRouter['shippingRates']['update']
export type DeleteShippingRatesOutput = OutputRouter['shippingRates']['delete']

// Input types
export type GetShippingRateInput = InputRouter['shippingRates']['getById']
export type ListShippingRatesInput = InputRouter['shippingRates']['list']
export type CreateShippingRateInput = InputRouter['shippingRates']['create']
export type UpdateShippingRateInput = InputRouter['shippingRates']['update']
export type DeleteShippingRatesInput = InputRouter['shippingRates']['delete']

// Query hooks
export const useShippingRate = (id: string, enabled = true) => {
    return trpc.shippingRates.getById.useQuery(
        { id },
        {
            enabled: enabled && !!id,
            retry: 2,
            staleTime: 30000,
        },
    )
}

export const useShippingRatesList = (params: ListShippingRatesInput, options?: {
    enabled?: boolean;
    refetchOnMount?: boolean | 'always';
    refetchOnWindowFocus?: boolean;
}) => {
    const { enabled = true, ...otherOptions } = options || {};
    console.log('params', params)
    return trpc.shippingRates.list.useQuery(params, {
        enabled,
        ...otherOptions,
    })
}

// Mutation hooks
export const useCreateShippingRate = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.shippingRates.create.useMutation({
        onSuccess: () => {
            utils.shippingRates.list.invalidate()
            toast({
                title: 'Success',
                description: 'Shipping rate created successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to create shipping rate',
                variant: 'destructive',
            })
        },
    })
}

export const useUpdateShippingRate = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.shippingRates.update.useMutation({
        onSuccess: () => {
            utils.shippingRates.list.invalidate()
            utils.shippingRates.getById.invalidate()
            toast({
                title: 'Success',
                description: 'Shipping rate updated successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update shipping rate',
                variant: 'destructive',
            })
        },
    })
}

export const useDeleteShippingRates = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.shippingRates.delete.useMutation({
        onSuccess: () => {
            utils.shippingRates.list.invalidate()
            toast({
                title: 'Success',
                description: 'Shipping rate(s) deleted successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to delete shipping rate(s)',
                variant: 'destructive',
            })
        },
    })
}

